import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "components/Modal/Modal";
import TextInput from "components/inputs/TextInput";
import TextArea from "components/inputs/TextAreaInput";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { generateId } from "utils/id";
import { useCreateRiskGroupingTemplate } from "components/Risk/hooks/useCreateRiskGroupingTemplate";

interface Props {
  show: boolean;
  onClose: () => void;
}

export const CreateRiskGroupingTemplateModal: React.FC<Props> = ({
  show,
  onClose,
}) => {
  return (
    <Modal
      show={show}
      onClose={onClose}
      width="1/2"
      content={<CreateRiskGroupingTemplateForm onClose={onClose} />}
    />
  );
};

interface FormProps {
  onClose: () => void;
}

const CreateRiskGroupingTemplateForm: React.FC<FormProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const createGroupingTemplate = useCreateRiskGroupingTemplate();
  const [payload, setPayload] = React.useState({ name: "", description: "" });
  const [isLoading, setIsLoading] = React.useState(false);

  const handleCreate = async () => {
    if (!payload.name) return;
    setIsLoading(true);
    createGroupingTemplate(
      {
        id: generateId(28),
        label: payload.name,
        description: payload.description,
        members: [],
      },
      {
        onSettled: () => {
          setIsLoading(false);
          onClose();
        },
      }
    );
  };

  return (
    <div>
      <span className="text-xl">
        {t("riskTemplate.groupings.create_modal_headline")}
      </span>
      <div className="pt-4 pb-4">
        <TextInput
          required
          uncontrolled
          id="grouping-name"
          label={t("riskTemplate.groupings.create_name_label")}
          defaultValue={payload.name}
          // @ts-ignore
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPayload((p) => ({ ...p, name: e.target.value }));
          }}
          placeholder={t("riskTemplate.groupings.create_name_placeholder")}
          onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
            if (evt.key === "Enter") {
              payload.name && handleCreate();
            }
          }}
        />
        <TextArea
          required
          uncontrolled
          id="grouping-description"
          name="grouping-description"
          label={t("riskTemplate.groupings.create_description_label")}
          value={payload.description}
          // @ts-ignore
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setPayload((p) => ({ ...p, description: e.target.value }));
          }}
          placeholder={t("riskTemplate.groupings.create_description_placeholder")}
        />
      </div>
      <div className="text-right flex justify-end items-center">
        <a
          className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
          onClick={onClose}
        >
          {t("risks.group.cancel")}
        </a>
        <PrimaryButton
          disabled={!payload.name || isLoading}
          onClick={handleCreate}
        >
          {isLoading
            ? t("riskTemplate.groupings.create_cta_loading")
            : t("riskTemplate.groupings.create_cta")}
        </PrimaryButton>
      </div>
    </div>
  );
};
